import {
  IPublicClientApplication,
  PublicClientApplication,
} from "@azure/msal-browser";
import {
  CustomAuthPublicClientApplication,
  ICustomAuthPublicClientApplication,
} from "@azure/msal-browser/custom-auth";
import { authRuntimeConfig, msalConfig } from "../authConfig";

let instance: IPublicClientApplication | null = null;
let nativeClient: ICustomAuthPublicClientApplication | null = null;

/**
 * Creates the app's MSAL instances.
 *
 * Two are needed, not one. CustomAuthPublicClientApplication is also a PublicClientApplication, but
 * its authority seeds that instance's authority-metadata cache with empty endpoints that never
 * expire, so a standard redirect on the same instance fails with endpoints_resolution_error.
 * Authority metadata is per instance and held in memory, while the token cache lives in
 * localStorage under the same client ID — so a separate standard instance resolves endpoints
 * normally and still sees accounts created by native sign-in.
 */
export async function createMsalInstance(): Promise<IPublicClientApplication> {
  if (process.env.NODE_ENV === "development") {
    console.info("[auth] config", {
      clientId: msalConfig.auth.clientId,
      authority: msalConfig.auth.authority,
      knownAuthorities: msalConfig.auth.knownAuthorities,
      redirectUri: msalConfig.auth.redirectUri,
      nativeAuthEnabled: authRuntimeConfig.nativeAuthEnabled,
      authApiProxyUrl: authRuntimeConfig.authApiProxyUrl,
    });
  }

  const pca = new PublicClientApplication(msalConfig);
  await pca.initialize();
  instance = pca;

  if (authRuntimeConfig.nativeAuthEnabled) {
    try {
      // create() also initialises the instance.
      nativeClient = await CustomAuthPublicClientApplication.create({
        ...msalConfig,
        customAuth: {
          challengeTypes: authRuntimeConfig.challengeTypes,
          authApiProxyUrl: authRuntimeConfig.authApiProxyUrl,
        },
      });
    } catch (error) {
      // Never block sign-in: without the native client every page uses the hosted flow.
      console.error("Native authentication failed to initialise; using hosted sign-in.", error);
      nativeClient = null;
    }
  }

  return pca;
}

/** The instance used for redirect and silent flows, and by MsalProvider. */
export function getMsalInstance(): IPublicClientApplication {
  if (!instance) {
    throw new Error("MSAL has not been initialised yet.");
  }
  return instance;
}

/** The native authentication client, or null when native auth is disabled or failed to start. */
export function getNativeAuthClient(): ICustomAuthPublicClientApplication | null {
  return nativeClient;
}
